import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { toast } from '@livable/saas-react-components';
import moment from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from 'src/api';
import { getSuggestionKey, toDateOnly } from 'src/components/allocations/AllocationSuggestion/suggestionModel';
import { chargeOnMonthOf, isChargeOnLocked, takesChargeOnDate } from './chargeOnDates';

// Straight through the api layer: the store actions cancel the previous call on the same action, and every table on a
// bill asks at once.
const NO_OPTIONS = {
  cancelToken: undefined
};
const NOT_SAVED = "The charge on date couldn't be saved. Please try again.";

/** The table's own date and the default, read from where Apply Billing reads them. */
async function readDate(_ref) {
  var _resp$response3;
  let billId = _ref.billId,
    month = _ref.month,
    propertyId = _ref.propertyId;
  if (billId) {
    var _resp$response$charge, _resp$response, _resp$response$defaul, _resp$response2;
    // The read needs a month. Where the table's can't be told (a closed month) this one only fills the field the read
    // requires, and only the table's own date is kept: a default worked out for another month would be wrong.
    const asked = month !== null && month !== void 0 ? month : {
      allocationCycleYear: moment().year(),
      allocationCycleMonth: moment().month() + 1
    };
    const resp = await api.AppfolioBills.getSettingsByBillIdV2(_objectSpread({
      billId,
      propertyId
    }, asked), NO_OPTIONS);
    return {
      chargeOnDate: (_resp$response$charge = resp === null || resp === void 0 || (_resp$response = resp.response) === null || _resp$response === void 0 ? void 0 : _resp$response.chargeOnDate) !== null && _resp$response$charge !== void 0 ? _resp$response$charge : null,
      defaultDate: month ? (_resp$response$defaul = resp === null || resp === void 0 || (_resp$response2 = resp.response) === null || _resp$response2 === void 0 ? void 0 : _resp$response2.defaultChargeOnDate) !== null && _resp$response$defaul !== void 0 ? _resp$response$defaul : null : null
    };
  }
  if (!month) return {
    chargeOnDate: null,
    defaultDate: null
  };
  const resp = await api.AppfolioProperties.getDefaultChargeOnDate(_objectSpread({
    propertyId
  }, month), NO_OPTIONS);
  return {
    chargeOnDate: null,
    defaultDate: (_resp$response3 = resp === null || resp === void 0 ? void 0 : resp.response) !== null && _resp$response3 !== void 0 ? _resp$response3 : null
  };
}

/** Which save of which date for which allocation bill: one automatic try each. */
const attemptOf = (billId, chargeOnDate) => "".concat(billId, "|").concat(chargeOnDate !== null && chargeOnDate !== void 0 ? chargeOnDate : '');

/**
 * The AppFolio charge on date of each allocation table on a bill (LB-6118, requested by Haley), for an AppFolio
 * company only. It is the appfolio-service bill setting Apply Billing reads and overrides, keyed by the allocation bill.
 *
 * Every date picked stays unsaved until a save of exactly that date has succeeded:
 * - On an allocated table it is saved at once; on one not allocated yet, once Allocate has made its allocation bill.
 * - Each unsaved date is saved on its own once per allocation bill. One that fails stays unsaved (and the table says
 *   so) until it is picked again or approving tries it again: it is never resent in a loop.
 * - Approving waits for these saves (`settle`), and goes ahead only when every date it would send is saved: the charges
 *   go to AppFolio with the date they have when approved. While it waits, and while `isBusy`, no date can change.
 */
export function useChargeOnDates(_ref2) {
  let allocatedTables = _ref2.allocatedTables,
    allocationStates = _ref2.allocationStates,
    enabled = _ref2.enabled,
    isBusy = _ref2.isBusy,
    monthInProgress = _ref2.monthInProgress,
    tables = _ref2.tables;
  // Kept in a ref so a save, a read and `settle` always see the latest; the counter re-renders the card.
  const entries = useRef({});
  const _useState = useState(0),
    _useState2 = _slicedToArray(_useState, 2),
    setVersion = _useState2[1];
  const states = useRef(allocationStates);
  // Saves on their way, by table, so approving can wait for them.
  const saving = useRef(new Map());
  // Tables an approval is waiting on, counted per table (approvals can overlap): their dates can't change meanwhile, and
  // a save failure is the approval's to report, not a toast of its own.
  const settlingCounts = useRef(new Map());
  // Each table's latest read, by parameters (to ask once) and by number (to drop an older answer landing last).
  const reads = useRef(new Map());
  const readCount = useRef(0);
  const _useState3 = useState(0),
    _useState4 = _slicedToArray(_useState3, 2),
    reloads = _useState4[0],
    setReloads = _useState4[1];
  const setEntry = useCallback((key, patch) => {
    entries.current = _objectSpread(_objectSpread({}, entries.current), {}, {
      [key]: _objectSpread(_objectSpread({}, entries.current[key] || {}), patch)
    });
    setVersion(version => version + 1);
  }, []);
  const billIdOf = useCallback(key => {
    var _states$current$key$a, _states$current;
    return (_states$current$key$a = (_states$current = states.current) === null || _states$current === void 0 || (_states$current = _states$current[key]) === null || _states$current === void 0 ? void 0 : _states$current.allocationBillId) !== null && _states$current$key$a !== void 0 ? _states$current$key$a : null;
  }, []);
  const wanted = enabled ? (tables || []).filter(takesChargeOnDate).map(table => {
    var _allocationStates$key, _allocationStates$key2;
    const key = getSuggestionKey(table.propertyId, table.utilityId);
    return {
      billId: (_allocationStates$key = allocationStates === null || allocationStates === void 0 || (_allocationStates$key2 = allocationStates[key]) === null || _allocationStates$key2 === void 0 ? void 0 : _allocationStates$key2.allocationBillId) !== null && _allocationStates$key !== void 0 ? _allocationStates$key : null,
      key,
      month: chargeOnMonthOf(table, allocatedTables === null || allocatedTables === void 0 ? void 0 : allocatedTables[key], monthInProgress),
      propertyId: table.propertyId
    };
  }) : [];
  const signature = JSON.stringify(wanted);
  useEffect(() => {
    JSON.parse(signature).forEach(item => {
      var _reads$current$get, _entries$current$item;
      const params = JSON.stringify(item);
      if (((_reads$current$get = reads.current.get(item.key)) === null || _reads$current$get === void 0 ? void 0 : _reads$current$get.params) === params) return;
      readCount.current += 1;
      const number = readCount.current;
      reads.current.set(item.key, {
        number,
        params
      });
      // Only the first read shows as loading: a read back after a save keeps the date on screen.
      setEntry(item.key, {
        isLoading: !((_entries$current$item = entries.current[item.key]) !== null && _entries$current$item !== void 0 && _entries$current$item.hasRead),
        propertyId: item.propertyId
      });
      readDate(item).catch(() => ({
        chargeOnDate: null,
        defaultDate: null
      })).then(read => {
        var _reads$current$get2;
        if (((_reads$current$get2 = reads.current.get(item.key)) === null || _reads$current$get2 === void 0 ? void 0 : _reads$current$get2.number) !== number) return;
        const _ref3 = entries.current[item.key] || {},
          pending = _ref3.pending;
        // A date picked and not saved yet is what is shown until it is.
        setEntry(item.key, _objectSpread(_objectSpread({}, read), {}, {
          chargeOnDate: pending === undefined ? read.chargeOnDate : pending,
          hasRead: true,
          isLoading: false
        }));
      });
    });
  }, [signature, reloads, setEntry]);

  /** Saves the date for the table's allocation bill. Resolves to whether it was saved. */
  const save = useCallback((key, billId, chargeOnDate) => {
    const _ref4 = entries.current[key] || {},
      propertyId = _ref4.propertyId;
    const promise = api.AppfolioBills.postBillSettings({
      billId,
      chargeOnDate,
      propertyId
    }, NO_OPTIONS).then(resp => (resp === null || resp === void 0 ? void 0 : resp.isSuccessful) !== false).catch(() => false).then(isSaved => {
      if (saving.current.get(key) === promise) saving.current.delete(key);
      const isLatestPick = (entries.current[key] || {}).pending === chargeOnDate;
      setEntry(key, _objectSpread(_objectSpread({
        isSaving: saving.current.has(key)
      }, isSaved && isLatestPick ? {
        chargeOnDate,
        failedAttempt: null,
        pending: undefined
      } : {}), !isSaved && isLatestPick ? {
        failedAttempt: attemptOf(billId, chargeOnDate)
      } : {}));
      if (!isSaved && !settlingCounts.current.get(key)) toast('error', {
        body: [NOT_SAVED]
      });
      if (isSaved) {
        // Read back what is stored, past any read that started before the save.
        reads.current.delete(key);
        setReloads(count => count + 1);
      }
      return isSaved;
    });
    saving.current.set(key, promise);
    setEntry(key, {
      isSaving: true
    });
    return promise;
  }, [setEntry]);

  // The latest allocation states, for the saves below and for `settle`. Before them, so they see this render's.
  useEffect(() => {
    states.current = allocationStates;
  });

  // Each unsaved date is saved on its own once its allocation bill exists, and once only per date and bill. It runs
  // after every render: the dates live in a ref, and whether one is due depends on the latest allocation states.
  useEffect(() => {
    Object.entries(entries.current).forEach(_ref5 => {
      let _ref6 = _slicedToArray(_ref5, 2),
        key = _ref6[0],
        entry = _ref6[1];
      const billId = billIdOf(key);
      if (entry.pending === undefined || !billId || saving.current.has(key)) return;
      if (entry.failedAttempt === attemptOf(billId, entry.pending)) return;
      save(key, billId, entry.pending);
    });
  });
  const change = useCallback((key, propertyId, picked) => {
    const chargeOnDate = toDateOnly(picked);
    // Saved by the effect above: at once on an allocated table, once allocated otherwise.
    setEntry(key, {
      chargeOnDate,
      failedAttempt: null,
      pending: chargeOnDate,
      propertyId
    });
  }, [setEntry]);

  /** Whether any of these tables has a date on its way or not saved yet, which approving has to wait for. */
  const hasUnsaved = keys => keys.some(key => {
    var _entries$current$key;
    return saving.current.has(key) || ((_entries$current$key = entries.current[key]) === null || _entries$current$key === void 0 ? void 0 : _entries$current$key.pending) !== undefined;
  });

  /**
   * Saves the dates of these tables that aren't saved yet, waiting for any on their way, and resolves to whether all
   * are saved. A failure is the caller's to report: approving then would send a date nobody chose.
   */
  const settle = useCallback(async keys => {
    const count = (key, by) => settlingCounts.current.set(key, (settlingCounts.current.get(key) || 0) + by);
    keys.forEach(key => count(key, 1));
    setVersion(version => version + 1);
    try {
      const results = await Promise.all(keys.map(async key => {
        const inFlight = saving.current.get(key);
        if (inFlight) await inFlight;
        const _ref7 = entries.current[key] || {},
          pending = _ref7.pending;
        if (pending === undefined) return true;
        const billId = billIdOf(key);
        return billId ? save(key, billId, pending) : false;
      }));
      return results.every(Boolean);
    } finally {
      keys.forEach(key => count(key, -1));
      setVersion(version => version + 1);
    }
  }, [billIdOf, save]);

  /** What the table's Charge on date control shows and does, or null where there is none. */
  const chargeOnFor = table => {
    var _entry$defaultDate, _entry$chargeOnDate;
    if (!enabled || !takesChargeOnDate(table)) return null;
    const key = getSuggestionKey(table.propertyId, table.utilityId);
    const entry = entries.current[key] || {};
    return {
      defaultDate: (_entry$defaultDate = entry.defaultDate) !== null && _entry$defaultDate !== void 0 ? _entry$defaultDate : null,
      // Picked and not saved: approving saves it first, and goes ahead only if that works.
      hasUnsavedDate: entry.pending !== undefined,
      id: "charge-on-date-".concat(key),
      isLoading: entry.isLoading !== false,
      // While approving, nothing changes the date the charges are about to be sent with.
      // Nor on a table scheduled for a later month: it can't be allocated before then, so a date picked would have
      // nowhere to be saved. Nor before the bill's allocation states are in (LB-6052): an approved table reads as one
      // nobody has allocated until then, and a date picked meanwhile would be saved onto its allocation bill.
      isLocked: isChargeOnLocked(table, allocatedTables === null || allocatedTables === void 0 ? void 0 : allocatedTables[key]) || !!table.isAwaitingStates || !!table.scheduledFor || !!isBusy || !!settlingCounts.current.get(key),
      isSaving: !!entry.isSaving,
      // Picked before the table is allocated: kept on this screen only, until Allocate makes the bill it is saved for.
      isWaitingForAllocation: entry.pending !== undefined && !billIdOf(key),
      onChange: picked => change(key, table.propertyId, picked),
      value: (_entry$chargeOnDate = entry.chargeOnDate) !== null && _entry$chargeOnDate !== void 0 ? _entry$chargeOnDate : null
    };
  };

  /** Whether an approval is waiting on a date: the card holds every other action meanwhile, as while it is busy. */
  const isSettling = [...settlingCounts.current.values()].some(Boolean);
  return {
    chargeOnFor,
    hasUnsaved,
    isSettling,
    settle
  };
}